<template>
  <div
    v-if="show"
    class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-auto flex items-center justify-center text-base"
  >
    <div class="bg-white rounded-lg shadow-xl w-11/12 md:w-1/3 max-h-[90vh]">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-lg text-gray-800 font-bold">
          Upload Services - {{ departmentName }}
        </h4>
        <button
          type="button"
          class="text-gray-500 hover:text-custom-yellow"
          @click="close"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 14 14">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <!-- Instructions -->
      <div class="px-6 pt-4 pb-2">
        <div class="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
          <p class="font-semibold text-blue-800 mb-1">Upload Instructions:</p>
          <ul class="list-disc list-inside text-blue-700 space-y-1">
            <li>File must be .csv or .xlsx format</li>
            <li>Include two columns: <strong>Service Name</strong> and <strong>Cost</strong></li>
            <li>First row should be headers</li>
            <li>Download template below for reference</li>
          </ul>
        </div>
      </div>

      <!-- Form -->
      <div class="px-6 pb-6">
        <div class="space-y-4">
          <!-- File Input -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              <span class="text-red-500">*</span> Select Upload File
              <span
                @click="downloadTemplate"
                class="text-sm text-green-700 cursor-pointer hover:underline ml-2"
              >
                (Download Template)
              </span>
            </label>
            <input
              id="fileInput"
              type="file"
              accept=".csv,.xlsx"
              @change="handleFileChange"
              class="w-full px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Select File"
            />
            <p v-if="fileError" class="text-red-500 text-sm mt-1">
              {{ fileError }}
            </p>
          </div>

          <!-- Selected File Info -->
          <div v-if="file" class="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p class="text-sm text-gray-700">
              <span class="font-semibold">Selected:</span> {{ file.name }}
            </p>
            <p class="text-xs text-gray-500 mt-1">
              Size: {{ formatFileSize(file.size) }}
            </p>
          </div>

          <!-- Upload Button -->
          <div>
            <CustomButton
              v-if="!isLoading"
              class="w-full text-center"
              label="Upload File"
              @click.prevent="uploadFile"
            />
            <CustomButton
              v-else
              class="w-full text-center opacity-50"
              label="Uploading..."
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomButton from "@/components/CustomButton.vue";
import apiClient from "@/services/api";

export default {
  name: "UploadServicesModal",
  components: { CustomButton },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    departmentId: {
      type: [Number, String],
      required: true,
    },
    departmentName: {
      type: String,
      required: true,
    },
    discipline: { type: String, default: null }
  },
  data() {
    return {
      file: null,
      fileError: null,
      isLoading: false,
    };
  },
  methods: {
    async downloadTemplate() {
      try {
        const response = await apiClient.downloadServicesTemplate();

        if (!response.ok) {
          throw new Error(`Failed to download template: ${response.status}`);
        }

        let filename = "services_template.xlsx";
        const disposition = response.headers.get("Content-Disposition");
        if (disposition) {
          const match = disposition.match(/filename="([^"]+)"/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        const blob = await response.blob();
        const typedBlob = new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(typedBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$toast.success("Template downloaded successfully");
      } catch (error) {
        console.error("Error downloading template:", error);
        this.$toast.error("Failed to download template. Please try again.");
      }
    },

    handleFileChange(event) {
      const input = event.target;
      const selectedFile = input.files?.[0] || null;

      this.fileError = null;

      if (selectedFile) {
        // Validate file type
        const validTypes = [
          "text/csv",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
        ];

        if (!validTypes.includes(selectedFile.type)) {
          this.fileError = "Please select a .csv or .xlsx file";
          this.file = null;
          input.value = "";
          return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (selectedFile.size > maxSize) {
          this.fileError = "File size must be less than 5MB";
          this.file = null;
          input.value = "";
          return;
        }

        this.file = selectedFile;
      } else {
        this.file = null;
      }
    },

    async uploadFile() {
      if (!this.file) {
        this.$toast.error("Please select a file to upload");
        return;
      }

      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("department_id", this.departmentId);
      if (this.discipline) formData.append("discipline", this.discipline)

      try {
        this.isLoading = true;
        const response = await apiClient.uploadDepartmentServices(formData);

        if (response.success) {
          this.$toast.success(
            response.message || "Services uploaded successfully"
          );
          this.$emit("refetch");
          this.close();
        } else {
          this.$toast.error(
            response.message || "Failed to upload services"
          );
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        this.$toast.error(
          error.message || "Failed to upload services. Please try again."
        );
      } finally {
        this.isLoading = false;
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
    },

    close() {
      // Reset form
      this.file = null;
      this.fileError = null;
      const fileInput = document.getElementById("fileInput");
      if (fileInput) {
        fileInput.value = "";
      }
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.bg-custom-yellow {
  background-color: #f59e0b;
}
</style>